// services/oattBillingService.ts
// REST client for OATT Billing Reservations API.
// Follows the same MSAL token injection pattern as avevaApiService.ts.

import axios, { AxiosInstance } from 'axios';
import { loginRequest } from '../authConfig';
import { getRuntimeEnv } from '@utils/runtimeEnv';

// ── Request / Response types ──────────────────────────────────────────────────

interface UploadMockDataParams {
  provider: string;
  billing_month: number;
  billing_year: number;
  response_json: unknown[];
  customer?: string;
  aref?: string;
}

interface UploadMockDataResult {
  success: boolean;
  action: 'created' | 'updated';
  id: number;
  message: string;
}

interface ImportReservationsParams {
  billing_month: number;
  billing_year: number;
  provider?: string;
}

interface ImportReservationsResult {
  success: boolean;
  mode: 'MOCK' | 'ORIGINAL';
  providersProcessed: number;
  rowsInserted: number;
  rowsUpdated: number;
  customersCreated: number;
  errors: { provider?: string; aref?: string | null; reason: string }[];
}

// ── Service class ─────────────────────────────────────────────────────────────

class OattBillingService {
  private readonly apiClient: AxiosInstance;
  private msalInstance: any;

  constructor() {
    const baseURL =
      getRuntimeEnv('REACT_APP_OATT_API_BASE') ||
      `${getRuntimeEnv('REACT_APP_SERVER_PROTOCOL')}://${getRuntimeEnv('REACT_APP_SERVER_HOST')}/api`;


    this.apiClient = axios.create({
      baseURL: `${baseURL}/oatt`,
      timeout: 60000,
      headers: { 'Content-Type': 'application/json' },
    });


    this.setupInterceptors();
  }

  setMsalInstance(msalInstance: any) {
    this.msalInstance = msalInstance;
  }

  private setupInterceptors() {
    this.apiClient.interceptors.request.use(
      async (config) => {
        if (this.msalInstance) {
          try {
            const account = this.msalInstance.getActiveAccount();
            if (account) {
              const tokenResponse = await this.msalInstance.acquireTokenSilent({
                ...loginRequest,
                account,
              });
              if (tokenResponse.accessToken) {
                config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
              }
            }
          } catch {
            // Continue – server will return 401 if token is truly missing
          }
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.apiClient.interceptors.response.use(
      (response) => response.data,
      (error) => {
        throw error.response?.data || error;
      }
    );
  }

  // ── Mock Storage Layer ────────────────────────────────────────────────────

  /**
   * Uploads a raw OATI mock payload for a single provider.
   *
   * STORAGE ONLY — does not process line items or write to oatt_data.
   * Uploading the same (provider, billing_month, billing_year) overwrites
   * the existing payload.
   */
  async uploadMockData(params: UploadMockDataParams): Promise<UploadMockDataResult> {
    return await (this.apiClient.post as any)('/mock/save', params);
  }

  // ── Import / Processing Layer ─────────────────────────────────────────────

  /**
   * Triggers import processing for all stored provider payloads for the
   * given billing period.
   *
   * Behaviour:
   *   MOCK mode     → loads all providers from billing_reservations_mock,
   *                   parses + flattens each payload, MERGEs into oatt_data
   *   ORIGINAL mode → calls live mTLS-authenticated OATI API, same processing
   *
   * Returns a summary: { providersProcessed, rowsInserted, rowsUpdated, errors }
   */
  async importReservations(
    params: ImportReservationsParams
  ): Promise<ImportReservationsResult> {
    return await (this.apiClient.post as any)('/fetch-reservations', params);
  }


}

export default new OattBillingService();
